import React, { useState, useCallback, useRef, useEffect } from 'react';
import { CodeEditor } from './CodeEditor';
import { validateCss } from './sanitizer';
import { codeTemplates } from './templates';
import { Code, Download, Upload, Wand2, AlertTriangle, X, GripHorizontal } from 'lucide-react';

// Utility: Debounce function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export const CustomHtmlEditorPanel = ({ content, onUpdate, onClose }) => {
  const [activeTab, setActiveTab] = useState('html');
  const [cssWarnings, setCssWarnings] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [panelHeight, setPanelHeight] = useState(40); // Height in vh
  const [isResizing, setIsResizing] = useState(false);

  const html = content.html || codeTemplates[0].html;
  const css = content.css || codeTemplates[0].css;

  // Create debounced update functions that persist across renders
  const debouncedHtmlUpdate = useRef(
    debounce((value, callback) => {
      if (callback) {
        callback('html', value);
      }
    }, 300)
  ).current;

  const debouncedCssUpdate = useRef(
    debounce((value, callback) => {
      if (callback) {
        callback('css', value);
      }
    }, 300)
  ).current;

  // Validate CSS
  React.useEffect(() => {
    const warnings = validateCss(css);
    setCssWarnings(warnings);
  }, [css]);

  // Handle code updates with debounce
  const updateHtml = useCallback((newHtml) => {
    debouncedHtmlUpdate(newHtml, onUpdate);
  }, [onUpdate, debouncedHtmlUpdate]);

  const updateCss = useCallback((newCss) => {
    debouncedCssUpdate(newCss, onUpdate);
  }, [onUpdate, debouncedCssUpdate]);

  // Handle panel resizing
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e) => {
      const viewportHeight = window.innerHeight;
      const distanceFromBottom = viewportHeight - e.clientY;
      const newHeightVh = (distanceFromBottom / viewportHeight) * 100;
      
      // Constrain between 20vh and 80vh
      const constrainedHeight = Math.min(Math.max(newHeightVh, 20), 80);
      setPanelHeight(constrainedHeight);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  // Template selection
  const applyTemplate = (template) => {
    if (onUpdate) {
      onUpdate('html', template.html);
      onUpdate('css', template.css);
    }
    setShowTemplates(false);
  };

  // Export/Import
  const exportCode = () => {
    const blob = new Blob(
      [JSON.stringify({ html, css }, null, 2)],
      { type: 'application/json' }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'custom-section.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const importCode = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const data = JSON.parse(event.target.result);
          if (data.html && onUpdate) {
            onUpdate('html', data.html);
          }
          if (data.css && onUpdate) {
            onUpdate('css', data.css);
          }
        } catch (err) {
          alert('Invalid file format');
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-gray-300 shadow-2xl z-40" style={{ height: `${panelHeight}vh` }}>
      {/* Resize Handle */}
      <div
        className="absolute top-0 left-0 right-0 h-2 cursor-ns-resize hover:bg-indigo-200 transition-colors flex items-center justify-center group"
        onMouseDown={() => setIsResizing(true)}
        style={{ marginTop: '-4px' }}
      >
        <div className="w-16 h-1 bg-gray-400 rounded-full group-hover:bg-indigo-500 transition-colors"></div>
        <GripHorizontal className="absolute w-4 h-4 text-gray-400 group-hover:text-indigo-600 transition-colors" />
      </div>
      
      {/* Toolbar */}
      <div className="flex justify-between items-center px-4 py-2 bg-gray-100 border-b border-gray-300">
        <div className="flex items-center gap-2">
          <Code className="w-4 h-4 text-gray-700" />
          <span className="text-sm font-semibold text-gray-900">Custom HTML & CSS Editor</span>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => setActiveTab('html')}
            className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
              activeTab === 'html'
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
            }`}
          >
            HTML
          </button>
          <button
            onClick={() => setActiveTab('css')}
            className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
              activeTab === 'css'
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
            }`}
          >
            CSS
          </button>
          
          <div className="w-px h-6 bg-gray-300 mx-2"></div>
          
          <button
            onClick={() => setShowTemplates(!showTemplates)}
            className="px-3 py-1.5 text-xs font-medium bg-white text-gray-700 rounded hover:bg-gray-50 border border-gray-300 flex items-center gap-1.5"
            title="Load template"
          >
            <Wand2 size={14} />
            Templates
          </button>

          <button
            onClick={exportCode}
            className="p-1.5 text-gray-700 rounded hover:bg-gray-100 border border-gray-300"
            title="Export code"
          >
            <Download size={14} />
          </button>

          <button
            onClick={importCode}
            className="p-1.5 text-gray-700 rounded hover:bg-gray-100 border border-gray-300"
            title="Import code"
          >
            <Upload size={14} />
          </button>
          
          <div className="w-px h-6 bg-gray-300 mx-2"></div>
          
          <button
            onClick={onClose}
            className="p-1.5 text-gray-700 rounded hover:bg-gray-100 hover:text-red-600"
            title="Close editor"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* CSS Warnings */}
      {cssWarnings.length > 0 && (
        <div className="px-4 py-2 bg-yellow-50 border-b border-yellow-200 flex items-center gap-2">
          <AlertTriangle size={16} className="text-yellow-600 flex-shrink-0" />
          <div className="text-xs text-yellow-900">
            {cssWarnings.map((warning, i) => <div key={i}>{warning}</div>)}
          </div>
        </div>
      )}

      {/* Templates Modal */}
      {showTemplates && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowTemplates(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-4">Choose a Template</h3>
            <div className="grid grid-cols-2 gap-3">
              {codeTemplates.map(template => (
                <button
                  key={template.id}
                  onClick={() => applyTemplate(template)}
                  className="p-3 border border-gray-200 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 text-left transition-colors"
                >
                  <div className="font-semibold text-sm text-gray-900">{template.name}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Code Editor */}
      <div className="h-full overflow-hidden bg-gray-900" style={{ height: `100%` }}>
        {activeTab === 'html' && (
          <CodeEditor
            value={html}
            onChange={updateHtml}
            language="html"
            height="100%"
          />
        )}
        {activeTab === 'css' && (
          <CodeEditor
            value={css}
            onChange={updateCss}
            language="css"
            height="100%"
          />
        )}
      </div>
    </div>
  );
};
